import { useState } from 'react'
import {
  CopyOutlined,
  DislikeFilled,
  DislikeOutlined,
  LikeFilled,
  LikeOutlined,
} from '@ant-design/icons'
import { Input, Modal, message } from 'antd'
import { api } from '../api'

export type QaCaseLabel = '' | 'good' | 'bad'

type Props = {
  messageId: number
  content: string
  caseLabel?: QaCaseLabel
  onCaseLabelChange: (label: QaCaseLabel) => void
}

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }
  const ta = document.createElement('textarea')
  ta.value = text
  ta.style.position = 'fixed'
  ta.style.opacity = '0'
  document.body.appendChild(ta)
  ta.select()
  document.execCommand('copy')
  document.body.removeChild(ta)
}

export function isPersistedChatMessageId(id: number) {
  return Number.isFinite(id) && id > 0 && id < 1_000_000_000
}

export function ChatMessageActions({ messageId, content, caseLabel = '', onCaseLabelChange }: Props) {
  const [busy, setBusy] = useState(false)
  const [badModalOpen, setBadModalOpen] = useState(false)
  const [badNotesDraft, setBadNotesDraft] = useState('')
  const good = caseLabel === 'good'
  const bad = caseLabel === 'bad'

  const setLabel = (next: QaCaseLabel, notes?: string) => {
    if (busy) return
    setBusy(true)
    api
      .patch<{ ok: boolean; case_label: string }>(`/public/chat/messages/${messageId}/feedback`, {
        case_label: next,
        ...(next === 'bad' && notes !== undefined ? { notes } : {}),
      })
      .then((r) => {
        const label = (r.data.case_label === 'good' || r.data.case_label === 'bad'
          ? r.data.case_label
          : '') as QaCaseLabel
        onCaseLabelChange(label)
        if (label === 'good') message.success('已标为 Good Case')
        else if (label === 'bad') message.success('已标为 Bad Case')
        else message.success('已清除分类')
      })
      .catch((e) => message.error(e.message || '标记失败'))
      .finally(() => setBusy(false))
  }

  const onBadClick = () => {
    if (bad) {
      setLabel('')
      return
    }
    setBadNotesDraft('')
    setBadModalOpen(true)
  }

  const onBadModalOk = () => {
    setBadModalOpen(false)
    setLabel('bad', badNotesDraft.trim())
  }

  const onCopy = () => {
    const text = content.trim()
    if (!text) return
    void copyText(text)
      .then(() => message.success('已复制回答'))
      .catch(() => message.error('复制失败'))
  }

  return (
    <>
      <div className="chat-msg-bubble-actions">
        <button
          type="button"
          className="chat-msg-bubble-action-btn"
          title="复制回答"
          aria-label="复制回答"
          onClick={onCopy}
        >
          <CopyOutlined />
        </button>
        <button
          type="button"
          className={`chat-msg-bubble-action-btn${good ? ' is-good' : ''}`}
          title={good ? '取消 Good Case' : '标为 Good Case'}
          aria-label="Good Case"
          aria-pressed={good}
          disabled={busy}
          onClick={() => setLabel(good ? '' : 'good')}
        >
          {good ? <LikeFilled /> : <LikeOutlined />}
        </button>
        <button
          type="button"
          className={`chat-msg-bubble-action-btn${bad ? ' is-bad' : ''}`}
          title={bad ? '取消 Bad Case' : '标为 Bad Case'}
          aria-label="Bad Case"
          aria-pressed={bad}
          disabled={busy}
          onClick={onBadClick}
        >
          {bad ? <DislikeFilled /> : <DislikeOutlined />}
        </button>
      </div>
      <Modal
        title="Bad Case 备注"
        open={badModalOpen}
        okText="确定"
        cancelText="取消"
        confirmLoading={busy}
        destroyOnClose
        onOk={onBadModalOk}
        onCancel={() => setBadModalOpen(false)}
      >
        <p style={{ marginBottom: 8, color: 'rgba(0,0,0,0.65)' }}>
          请简要说明该回答的问题或期望结果，备注将同步到后台「对话评测」。
        </p>
        <Input.TextArea
          rows={4}
          maxLength={512}
          showCount
          value={badNotesDraft}
          onChange={(e) => setBadNotesDraft(e.target.value)}
          placeholder="例如：回答遗漏关键条款、引用文档错误、语气不当等"
          autoFocus
        />
      </Modal>
    </>
  )
}
